import React, { useState, useEffect, createContext, useContext } from "react";
import "./App.css";
import "./styles/mobile.css";
import { BrowserRouter, Routes, Route, Navigate, useNavigate } from "react-router-dom";
import axios from "axios";
import Components from "./Components";
import TaskDetailsPage from "./components/tasks/TaskDetailsPage";
import ContactDetailsPage from "./components/contacts/ContactDetailsPage";
import PaymentPage from "./components/payment/PaymentPage";
import PaymentSuccessPage from "./components/payment/PaymentSuccessPage";
import PaymentCancelledPage from "./components/payment/PaymentCancelledPage";
import ArchivesPage from "./components/tasks/ArchivesPage";
import { WorkspaceProvider } from "./contexts/WorkspaceContext";
import { DataProvider } from "./contexts/DataContext";
import { LanguageProvider, useLanguage } from "./contexts/LanguageContext";
import ReminderManager from "./components/common/ReminderManager";
import SwipeBack from "./components/common/SwipeBack";
import OfflineBanner from "./components/common/OfflineBanner";
import OfflineSyncManager from "./components/common/OfflineSyncManager";
import { registerServiceWorker } from "./utils/serviceWorker";
import { installOfflineInterceptor, drainQueue } from "./utils/offlineWriteQueue";
import { isNetworkError } from "./utils/networkStatus";
import AcceptInvitationPage from "./components/organization/AcceptInvitationPage";
import PublicSharedTaskPage from "./components/public/PublicSharedTaskPage";
import { Toaster } from "sonner";
import ConfirmDialogHost from "./components/common/ConfirmDialogHost";

// Install the offline write interceptor exactly once, at module load. We do it
// outside the React tree so it is in place before any component fires axios.
installOfflineInterceptor();

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

/*
 * PasswordInput — a tiny wrapper around <input type="password"> that
 * adds a show/hide eye button on the right edge of the field. Used by
 * the signup, login, password-reset and forgot-password-reset forms so
 * users can verify what they're typing.
 *
 * We keep the visibility state LOCAL to each input so toggling the
 * "Password" field doesn't unmask the "Confirm Password" field next to
 * it — that would partly defeat the purpose of a confirmation field.
 */
const PasswordInput = React.forwardRef(({ className = '', testIdBase, ...rest }, ref) => {
  const [show, setShow] = useState(false);
  const baseClass = className || 'w-full px-3 py-3 border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent';
  return (
    <div className="relative">
      <input
        ref={ref}
        {...rest}
        type={show ? 'text' : 'password'}
        className={`${baseClass} pr-11`}
        data-testid={testIdBase ? `${testIdBase}-input` : undefined}
      />
      <button
        type="button"
        onClick={() => setShow((s) => !s)}
        aria-label={show ? 'Hide password' : 'Show password'}
        title={show ? 'Hide password' : 'Show password'}
        data-testid={testIdBase ? `${testIdBase}-toggle` : undefined}
        className="absolute right-2 top-1/2 -translate-y-1/2 p-2 text-gray-500 hover:text-gray-700 focus:outline-none focus:ring-2 focus:ring-blue-500 rounded-md"
        tabIndex={-1}
      >
        {show ? (
          // Eye-off
          <svg xmlns="http://www.w3.org/2000/svg" className="w-5 h-5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
            <path d="M17.94 17.94A10.94 10.94 0 0 1 12 20c-7 0-11-8-11-8a21.6 21.6 0 0 1 5.16-5.94" />
            <path d="M9.9 4.24A10.94 10.94 0 0 1 12 4c7 0 11 8 11 8a21.78 21.78 0 0 1-3.16 4.19" />
            <path d="M14.12 14.12a3 3 0 1 1-4.24-4.24" />
            <line x1="1" y1="1" x2="23" y2="23" />
          </svg>
        ) : (
          // Eye
          <svg xmlns="http://www.w3.org/2000/svg" className="w-5 h-5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
            <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" />
            <circle cx="12" cy="12" r="3" />
          </svg>
        )}
      </button>
    </div>
  );
});
PasswordInput.displayName = 'PasswordInput';

// Auth Context
const AuthContext = createContext();

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const validateAndRestoreAuth = async () => {
      const token = localStorage.getItem("token");
      const userData = localStorage.getItem("user");
      
      if (token && userData) {
        try {
          const parsedUser = JSON.parse(userData);
          
          // Set token in axios headers for validation request
          axios.defaults.headers.common["Authorization"] = `Bearer ${token}`;
          
          // Validate token with backend by making a test request
          try {
            await axios.get(`${API}/auth/verify-token`);
            
            // Token is valid, restore auth state
            setUser(parsedUser);
            console.log("Auth restored and validated:", parsedUser);
            // Drain any offline operations that were queued before we shut down.
            drainQueue();
          } catch (validationError) {
            // Distinguish "token is bad" from "we can't reach the server".
            // When the user is offline, the verify-token call dies with a
            // network error (no `response`). In that case, we trust the
            // locally stored session so the app stays usable offline and
            // doesn't bounce the user to the login screen on every reload.
            if (isNetworkError(validationError) || !validationError.response) {
              console.warn("Token verification skipped — appears offline. Restoring local session.");
              setUser(parsedUser);
              // The drain will retry once the network comes back; harmless to
              // call now since it short-circuits when offline.
              drainQueue();
            } else {
              console.warn("Token validation failed, clearing session:", validationError.response?.status);
              localStorage.removeItem("token");
              localStorage.removeItem("user");
              delete axios.defaults.headers.common["Authorization"];
              setUser(null);
            }
          }
        } catch (parseError) {
          console.error("Error parsing user data from localStorage:", parseError);
          localStorage.removeItem("token");
          localStorage.removeItem("user");
          delete axios.defaults.headers.common["Authorization"];
        }
      }
      
      setLoading(false);
    };
    
    validateAndRestoreAuth();
  }, []);

  const login = async (email, password) => {
    try {
      console.log("Login attempt for:", email); // Debug log
      const response = await axios.post(`${API}/auth/login`, { email, password });
      const { access_token, user: userData } = response.data;
      
      console.log("Login successful, user data:", userData); // Debug log
      localStorage.setItem("token", access_token);
      localStorage.setItem("user", JSON.stringify(userData));
      axios.defaults.headers.common["Authorization"] = `Bearer ${access_token}`;
      setUser(userData);
      console.log("User state set to:", userData); // Debug log
      // Now that we're authenticated, drain anything that was queued offline
      // before login (or during a previous session).
      drainQueue();
      
      return { success: true };
    } catch (error) {
      console.error("Login error:", error);
      console.error("Error details:", {
        message: error.message,
        response: error.response,
        request: error.request,
        config: error.config
      });
      
      let errorMessage = "Login failed";
      
      // Network error (CORS, connection failed, etc.)
      if (error.message === 'Network Error' || !error.response) {
        errorMessage = "Cannot connect to server. Please check your internet connection.";
        console.error("Network error - possibly CORS or server unavailable");
      }
      // Server responded with error
      else if (error.response?.data) {
        if (typeof error.response.data === 'string') {
          errorMessage = error.response.data;
        } else if (error.response.data.detail) {
          errorMessage = error.response.data.detail;
        } else if (error.response.data.message) {
          errorMessage = error.response.data.message;
        }
      }
      // HTTP status error
      else if (error.response?.status) {
        errorMessage = `Login failed (Error ${error.response.status})`;
      }
      
      return { 
        success: false, 
        error: errorMessage 
      };
    }
  };

  const register = async (name, email, password) => {
    try {
      console.log("Registration attempt:", { name, email });
      const response = await axios.post(`${API}/auth/register`, { name, email, password });
      console.log("Registration response:", response.status, response.data);
      
      // Registration returns { message, user_id, is_verified }
      // User needs to verify email before logging in (unless admin)
      const { message, is_verified } = response.data;
      
      if (is_verified) {
        // Admin users are auto-verified, they can login immediately
        console.log("Admin user registered, redirecting to login");
        return { 
          success: true, 
          message: message || "Registration successful! Please log in.",
          isAdmin: true 
        };
      } else {
        // Regular users need to verify email
        console.log("Registration successful, email verification required");
        return { 
          success: true, 
          message: message || "Registration successful! Please check your email to verify your account.",
          requiresVerification: true
        };
      }
    } catch (error) {
      console.error("Registration error:", error);
      console.error("Error details:", {
        message: error.message,
        response: error.response,
        request: error.request
      });
      
      let errorMessage = "Registration failed";
      
      // Network error (CORS, connection failed, etc.)
      if (error.message === 'Network Error' || !error.response) {
        errorMessage = "Cannot connect to server. Please check your internet connection.";
        console.error("Network error - possibly CORS or server unavailable");
      }
      // Server responded with error
      else if (error.response?.data) {
        if (typeof error.response.data === 'string') {
          errorMessage = error.response.data;
        } else if (error.response.data.detail) {
          errorMessage = error.response.data.detail;
        } else if (error.response.data.message) {
          errorMessage = error.response.data.message;
        }
      }
      // HTTP status error
      else if (error.response?.status) {
        errorMessage = `Registration failed (Error ${error.response.status})`;
      }
      
      return { 
        success: false, 
        error: errorMessage 
      };
    }
  };

  const logout = () => {
    localStorage.removeItem("token");
    localStorage.removeItem("user");
    delete axios.defaults.headers.common["Authorization"];
    setUser(null);
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-blue-50 to-indigo-100">
        <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-blue-500"></div>
      </div>
    );
  }

  return (
    <AuthContext.Provider value={{ user, login, register, logout }}>
      {children}
    </AuthContext.Provider>
  );
};

// Reset Password Form Component
// Email Verification Page Component (for email verification links)
const EmailVerificationPage = () => {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState(false);
  const navigate = useNavigate();

  // Cache the token in state on FIRST mount — see comment in
  // ResetPasswordPage for why this matters (BackButtonHandler used to
  // strip the `?token=` query off the URL on mount, breaking re-reads).
  const [token] = useState(() => {
    try {
      const params = new URLSearchParams(window.location.search);
      return params.get('token') || '';
    } catch { return ''; }
  });

  useEffect(() => {
    if (!token) {
      setError("Invalid verification link");
      setLoading(false);
      return;
    }

    const verifyEmail = async () => {
      try {
        const response = await axios.get(`${API}/auth/verify-email?token=${token}`);
        
        if (response.data.success) {
          setSuccess(true);
          setTimeout(() => {
            navigate('/login');
          }, 3000);
        } else {
          setError("Verification failed");
        }
      } catch (error) {
        console.error("Email verification error:", error);
        setError(error.response?.data?.detail || "Verification failed. The link may be expired.");
      } finally {
        setLoading(false);
      }
    };

    verifyEmail();
  }, [token, navigate]);

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-400 via-purple-500 to-indigo-600 flex items-center justify-center p-4">
      <div className="w-full max-w-md">
        <div className="bg-white rounded-2xl shadow-xl p-8 text-center">
          <div className="mb-6">
            <div className="w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4" style={{background: '#EFF6FF'}}>
              {loading ? (
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500"></div>
              ) : success ? (
                <span className="text-2xl">✅</span>
              ) : (
                <span className="text-2xl">❌</span>
              )}
            </div>
            
            <h1 className="text-2xl font-bold text-gray-900 mb-2">
              {loading ? "Verifying Email..." : success ? "Email Verified!" : "Verification Failed"}
            </h1>
            
            {loading && (
              <p className="text-gray-600">Please wait while we verify your email address.</p>
            )}
            
            {success && (
              <div>
                <p className="mb-4" style={{color: '#10B981'}}>
                  Your email has been successfully verified! You can now log in to your account.
                </p>
                <p className="text-sm text-gray-500">
                  Redirecting to login page in 3 seconds...
                </p>
              </div>
            )}
            
            {error && (
              <div>
                <p className="mb-4" style={{color: '#EF4444'}}>{error}</p>
                <button
                  onClick={() => navigate('/login')}
                  className="text-white py-2 px-4 rounded-lg text-sm font-medium transition-colors"
                  style={{ 
                    background: 'linear-gradient(135deg, #2563EB 0%, #3B82F6 100%)',
                    boxShadow: '0 4px 12px rgba(37, 99, 235, 0.4)'
                  }}
                  onMouseEnter={(e) => e.target.style.boxShadow = '0 6px 16px rgba(37, 99, 235, 0.5)'}
                  onMouseLeave={(e) => e.target.style.boxShadow = '0 4px 12px rgba(37, 99, 235, 0.4)'}
                >
                  Go to Login
                </button>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

// Reset Password Page Component (for email links)
const ResetPasswordPage = () => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState(false);
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const navigate = useNavigate();

  // Cache the token in state on FIRST mount. The previous version read
  // `window.location.search` on every render — fragile because
  // BackButtonHandler used to call `history.pushState` with a path-only
  // URL on mount, stripping the `?token=...` and flashing "Invalid Reset
  // Link" the moment the user typed in the form. Reading it once into
  // state guarantees the token survives all subsequent re-renders.
  const [token] = useState(() => {
    try {
      const params = new URLSearchParams(window.location.search);
      return params.get('token') || '';
    } catch { return ''; }
  });

  const handleResetSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError("");

    if (newPassword.length < 6) {
      setError("Password must be at least 6 characters long");
      setLoading(false);
      return;
    }

    if (newPassword !== confirmPassword) {
      setError("Passwords do not match");
      setLoading(false);
      return;
    }

    try {
      await axios.post(`${API}/auth/reset-password`, {
        token: token,
        new_password: newPassword
      });
      
      setSuccess(true);
      setTimeout(() => {
        navigate('/login');
      }, 3000);
    } catch (error) {
      console.error("Reset password error:", error);
      setError(error.response?.data?.detail || "Failed to reset password. The link may be expired.");
    } finally {
      setLoading(false);
    }
  };

  if (!token) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-purple-400 via-pink-500 to-red-500 flex items-center justify-center p-4">
        <div className="bg-white rounded-2xl shadow-2xl p-8 max-w-md w-full">
          <div className="text-center">
            <h2 className="text-2xl font-bold text-gray-900 mb-4">Invalid Reset Link</h2>
            <p className="text-gray-600 mb-6">This password reset link is invalid or has expired.</p>
            <button
              onClick={() => navigate('/login')}
              className="w-full bg-gradient-to-r from-blue-500 to-indigo-600 text-white py-3 rounded-xl font-medium hover:from-blue-600 hover:to-indigo-700 transition-all duration-200"
            >
              Back to Login
            </button>
          </div>
        </div>
      </div>
    );
  }

  if (success) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-purple-400 via-pink-500 to-red-500 flex items-center justify-center p-4">
        <div className="bg-white rounded-2xl shadow-2xl p-8 max-w-md w-full">
          <div className="text-center">
            <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <svg className="w-8 h-8 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7" />
              </svg>
            </div>
            <h2 className="text-2xl font-bold text-gray-900 mb-4">Password Reset Successful!</h2>
            <p className="text-gray-600 mb-6">Your password has been updated successfully. You will be redirected to the login page.</p>
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-purple-600 mx-auto"></div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-purple-400 via-pink-500 to-red-500 flex items-center justify-center p-4">
      <div className="bg-white rounded-2xl shadow-2xl p-8 max-w-md w-full">
        <div className="text-center mb-8">
          <h2 className="text-3xl font-bold text-gray-900 mb-2">Reset Your Password</h2>
          <p className="text-gray-600">Enter your new password below</p>
        </div>

        <form onSubmit={handleResetSubmit} className="space-y-6">
          {error && (
            <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-xl">
              {error}
            </div>
          )}

          <div>
            <label className="block text-gray-700 text-sm font-medium mb-2">New Password</label>
            <PasswordInput
              value={newPassword}
              onChange={(e) => setNewPassword(e.target.value)}
              required
              minLength="6"
              placeholder="Enter new password (min. 6 characters)"
              testIdBase="reset-new-password"
            />
          </div>

          <div>
            <label className="block text-gray-700 text-sm font-medium mb-2">Confirm New Password</label>
            <PasswordInput
              value={confirmPassword}
              onChange={(e) => setConfirmPassword(e.target.value)}
              required
              minLength="6"
              placeholder="Confirm your new password"
              testIdBase="reset-confirm-password"
            />
          </div>

          <button
            type="submit"
            disabled={loading}
            className="w-full bg-gradient-to-r from-blue-500 to-indigo-600 text-white py-3 rounded-xl font-medium hover:from-blue-600 hover:to-indigo-700 transition-all duration-200 transform hover:scale-105 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {loading ? "Updating Password..." : "Update Password"}
          </button>
        </form>

        <div className="mt-6 text-center">
          <button
            onClick={() => navigate('/login')}
            className="text-blue-600 hover:text-blue-800 text-sm font-medium"
          >
            Back to Login
          </button>
        </div>
      </div>
    </div>
  );
};

const ResetPasswordForm = ({ resetToken, onSuccess, onBack }) => {
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (newPassword !== confirmPassword) {
      setError("Passwords don't match");
      return;
    }

    if (newPassword.length < 6) {
      setError("Password must be at least 6 characters long");
      return;
    }

    setLoading(true);
    setError("");

    try {
      await axios.post(`${API}/auth/reset-password`, {
        token: resetToken,
        new_password: newPassword
      });
      
      onSuccess();
    } catch (error) {
      console.error("Reset password error:", error);
      setError(error.response?.data?.detail || "Failed to reset password. Please try again.");
    }
    
    setLoading(false);
  };

  return (
    <form onSubmit={handleSubmit}>
      {error && (
        <div className="mb-4 p-3 bg-red-100 border border-red-400 text-red-700 rounded-lg text-sm">
          {error}
        </div>
      )}
      
      <div className="mb-4">
        <label className="block text-gray-700 text-sm font-medium mb-2">New Password</label>
        <PasswordInput
          value={newPassword}
          onChange={(e) => setNewPassword(e.target.value)}
          required
          minLength="6"
          testIdBase="forgot-new-password"
        />
      </div>

      <div className="mb-6">
        <label className="block text-gray-700 text-sm font-medium mb-2">Confirm Password</label>
        <PasswordInput
          value={confirmPassword}
          onChange={(e) => setConfirmPassword(e.target.value)}
          required
          minLength="6"
          testIdBase="forgot-confirm-password"
        />
      </div>

      <button
        type="submit"
        disabled={loading}
        className="w-full bg-gradient-to-r from-blue-500 to-indigo-600 text-white py-3 rounded-xl font-medium hover:from-blue-600 hover:to-indigo-700 transition-all duration-200 transform hover:scale-105 disabled:opacity-50 disabled:cursor-not-allowed mb-4"
      >
        {loading ? "Resetting..." : "Reset Password"}
      </button>

      <div className="text-center">
        <button
          type="button"
          onClick={onBack}
          className="text-blue-600 hover:text-blue-800 text-sm font-medium"
        >
          Back
        </button>
      </div>
    </form>
  );
};

// Login Component
const Login = () => {
  const [isLogin, setIsLogin] = useState(true);
  const [formData, setFormData] = useState({ name: "", email: "", password: "", confirmPassword: "" });
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [showForgotPassword, setShowForgotPassword] = useState(false);
  const [forgotPasswordEmail, setForgotPasswordEmail] = useState("");
  const [resetMessage, setResetMessage] = useState("");
  const [resetToken, setResetToken] = useState("");
  const [showResetPassword, setShowResetPassword] = useState(false);
  const [agreeToTerms, setAgreeToTerms] = useState(false);
  const [showTermsModal, setShowTermsModal] = useState(false);
  // Resend verification email support (banner CTA after signup)
  const [pendingVerifyEmail, setPendingVerifyEmail] = useState("");
  const [resendState, setResendState] = useState("idle"); // idle | sending | sent | error
  const [resendCooldown, setResendCooldown] = useState(0);
  const { login, register } = useAuth();
  const { t } = useLanguage();
  const navigate = useNavigate();

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError("");    if (formData.password.length < 6) {
      setError("Password must be at least 6 characters long");
      setLoading(false);
      return;
    }

    // Check password confirmation for signup
    if (!isLogin && formData.password !== formData.confirmPassword) {
      setError("Passwords do not match");
      setLoading(false);
      return;
    }

    // Check terms agreement for signup
    if (!isLogin && !agreeToTerms) {
      setError("Please agree to the terms and service to continue");
      setLoading(false);
      return;
    }

    const result = isLogin 
      ? await login(formData.email, formData.password)
      : await register(formData.name, formData.email, formData.password);

    if (result.success) {
      if (result.requiresVerification) {
        // Show success message for email verification
        setError(""); // Clear any previous errors
        setResetMessage(result.message || "Registration successful! Please check your email to verify your account.");
        setShowForgotPassword(false); // Ensure we're on the login view
        setIsLogin(true); // Switch to login mode
        setPendingVerifyEmail(formData.email); // Remember for the "Resend" link
        setResendState("idle");
        // Clear form
        setFormData({
          name: '',
          email: '',
          password: '',
          confirmPassword: ''
        });
      } else {
        // Admin user or already verified, redirect to home
        setTimeout(() => {
          navigate('/');
        }, 500);
      }
    } else {
      setError(result.error);
    }
    setLoading(false);
  };

  const handleInputChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handleForgotPasswordSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError("");

    try {
      const response = await axios.post(`${API}/auth/forgot-password`, {
        email: forgotPasswordEmail
      });
      
      setResetMessage(response.data.message);
      
      // Clear the email field after successful submission
      setForgotPasswordEmail("");
      
      // Check for demo emails after a short delay
      setTimeout(async () => {
        try {
          const emailsResponse = await axios.get(`${API}/auth/demo-emails/${forgotPasswordEmail}`);
          if (emailsResponse.data.emails && emailsResponse.data.emails.length > 0) {
            const latestEmail = emailsResponse.data.emails[0];
            setResetToken(latestEmail.reset_token);
          }
        } catch (error) {
          console.log("No demo emails found (normal for production)");
        }
      }, 1000);
      
    } catch (error) {
      console.error("Forgot password error:", error);
      setError(error.response?.data?.detail || "Failed to process password reset request.");
    }
    
    setLoading(false);
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-blue-50 to-indigo-100">
      {/* Terms and Service Modal */}
      {showTermsModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl shadow-2xl max-w-2xl w-full max-h-[80vh] overflow-hidden flex flex-col">
            {/* Modal Header */}
            <div className="bg-gradient-to-r from-blue-500 to-indigo-600 text-white px-6 py-4 flex items-center justify-between">
              <h2 className="text-2xl font-bold">Terms and Service</h2>
              <button
                onClick={() => setShowTermsModal(false)}
                className="text-white hover:text-gray-200 transition-colors"
              >
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
            
            {/* Modal Content */}
            <div className="px-6 py-4 overflow-y-auto flex-1">
              <div className="prose prose-sm max-w-none">
                <h3 className="text-lg font-semibold text-gray-900 mb-3">1. Acceptance of Terms</h3>
                <p className="text-gray-700 mb-4">
                  By accessing and using Edotask ("the Service"), you accept and agree to be bound by the terms and provisions of this agreement.
                </p>

                <h3 className="text-lg font-semibold text-gray-900 mb-3">2. Use License</h3>
                <p className="text-gray-700 mb-4">
                  Permission is granted to temporarily use Edotask for personal, non-commercial transitory viewing only. This is the grant of a license, not a transfer of title, and under this license you may not:
                </p>
                <ul className="list-disc list-inside text-gray-700 mb-4 space-y-1">
                  <li>Modify or copy the materials</li>
                  <li>Use the materials for any commercial purpose</li>
                  <li>Attempt to decompile or reverse engineer any software contained on Edotask</li>
                  <li>Remove any copyright or other proprietary notations from the materials</li>
                </ul>

                <h3 className="text-lg font-semibold text-gray-900 mb-3">3. User Account</h3>
                <p className="text-gray-700 mb-4">
                  You are responsible for maintaining the confidentiality of your account and password. You agree to accept responsibility for all activities that occur under your account.
                </p>

                <h3 className="text-lg font-semibold text-gray-900 mb-3">4. Privacy Policy</h3>
                <p className="text-gray-700 mb-4">
                  Your use of Edotask is also governed by our Privacy Policy. We collect and use your personal information to provide and improve the Service. By using Edotask, you consent to the collection and use of information in accordance with our Privacy Policy.
                </p>

                <h3 className="text-lg font-semibold text-gray-900 mb-3">5. User Content</h3>
                <p className="text-gray-700 mb-4">
                  You retain all rights to any content you submit, post or display on or through the Service. By submitting content, you grant us a worldwide, non-exclusive, royalty-free license to use, copy, reproduce, process, adapt, and publish your content for the purpose of providing the Service.
                </p>

                <h3 className="text-lg font-semibold text-gray-900 mb-3">6. Prohibited Activities</h3>
                <p className="text-gray-700 mb-4">
                  You agree not to engage in any of the following prohibited activities:
                </p>
                <ul className="list-disc list-inside text-gray-700 mb-4 space-y-1">
                  <li>Using the Service for any illegal purpose</li>
                  <li>Attempting to gain unauthorized access to the Service</li>
                  <li>Interfering with or disrupting the Service</li>
                  <li>Collecting or storing personal data about other users</li>
                </ul>

                <h3 className="text-lg font-semibold text-gray-900 mb-3">7. Disclaimer</h3>
                <p className="text-gray-700 mb-4">
                  The Service is provided "as is". Edotask makes no warranties, expressed or implied, and hereby disclaims and negates all other warranties including, without limitation, implied warranties or conditions of merchantability, fitness for a particular purpose, or non-infringement of intellectual property.
                </p>

                <h3 className="text-lg font-semibold text-gray-900 mb-3">8. Limitation of Liability</h3>
                <p className="text-gray-700 mb-4">
                  In no event shall Edotask or its suppliers be liable for any damages (including, without limitation, damages for loss of data or profit, or due to business interruption) arising out of the use or inability to use Edotask.
                </p>

                <h3 className="text-lg font-semibold text-gray-900 mb-3">9. Modifications to Terms</h3>
                <p className="text-gray-700 mb-4">
                  Edotask reserves the right to revise these terms of service at any time without notice. By using this Service you are agreeing to be bound by the then current version of these terms of service.
                </p>

                <h3 className="text-lg font-semibold text-gray-900 mb-3">10. Contact Information</h3>
                <p className="text-gray-700 mb-4">
                  If you have any questions about these Terms and Service, please contact us at <a href="mailto:support@edotask.com" className="text-blue-600 hover:text-blue-800 underline">support@edotask.com</a>
                </p>

                <p className="text-sm text-gray-500 mt-6">
                  Last updated: October 2025
                </p>
              </div>
            </div>
            
            {/* Modal Footer */}
            <div className="bg-gray-50 px-6 py-4 flex items-center justify-between border-t">
              <button
                onClick={() => setShowTermsModal(false)}
                className="px-6 py-2 bg-gray-300 text-gray-700 rounded-lg font-medium hover:bg-gray-400 transition-colors"
              >
                Close
              </button>
              <button
                onClick={() => {
                  setAgreeToTerms(true);
                  setShowTermsModal(false);
                }}
                className="px-6 py-2 bg-gradient-to-r from-blue-500 to-indigo-600 text-white rounded-lg font-medium hover:from-blue-600 hover:to-indigo-700 transition-colors"
              >
                I Agree
              </button>
            </div>
          </div>
        </div>
      )}
      
      <div className="max-w-md w-full mx-4">
        <div className="bg-white rounded-2xl shadow-xl p-8">
          <div className="text-center mb-8">
            <img 
              src="/logos/edotask-full-logo.png" 
              alt="Edotask" 
              className="h-8 mb-4 mx-auto"
              style={{maxWidth: '200px', height: 'auto'}}
            />
            <p className="text-gray-600">
              {showForgotPassword ? "Reset your password" : 
               showResetPassword ? "Set new password" :
               (isLogin ? "Welcome back!" : "Join Edotask today")}
            </p>
          </div>

          {error && (
            <div className="mb-4 p-3 bg-red-100 border border-red-400 text-red-700 rounded-lg text-sm">
              {typeof error === 'string' ? error : 'An error occurred'}
            </div>
          )}

          {showForgotPassword ? (
            <form onSubmit={handleForgotPasswordSubmit}>
              <div className="mb-6">
                <label className="block text-gray-700 text-sm font-medium mb-2">Email</label>
                <input
                  type="email"
                  value={forgotPasswordEmail}
                  onChange={(e) => setForgotPasswordEmail(e.target.value)}
                  className="w-full px-3 py-3 border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  required
                />
              </div>

              <button
                type="submit"
                disabled={loading}
                className="w-full bg-gradient-to-r from-blue-500 to-indigo-600 text-white py-3 rounded-xl font-medium hover:from-blue-600 hover:to-indigo-700 transition-all duration-200 transform hover:scale-105 disabled:opacity-50 disabled:cursor-not-allowed mb-4"
              >
                {loading ? "Sending..." : "Send Reset Link"}
              </button>

              <div className="text-center">
                <button
                  type="button"
                  onClick={() => {
                    setShowForgotPassword(false);
                    setForgotPasswordEmail(""); // Clear email field
                    setResetMessage(""); // Clear success message
                    setError(""); // Clear any error
                  }}
                  className="text-blue-600 hover:text-blue-800 text-sm font-medium"
                >
                  Back to Sign In
                </button>
              </div>

              {resetMessage && (
                <div className="mt-4 p-4 bg-green-50 border border-green-200 rounded-xl">
                  <div className="flex items-start space-x-3">
                    <svg className="w-6 h-6 text-green-600 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M3 8l7.89 4.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                    </svg>
                    <div className="flex-1">
                      <p className="text-green-800 font-medium">Email Sent!</p>
                      <p className="text-green-700 text-sm mt-1">{resetMessage}</p>
                      <p className="text-green-600 text-xs mt-2">Please check your email inbox and spam folder.</p>
                    </div>
                  </div>
                  
                  {resetToken && (
                    <div className="mt-4 p-3 bg-blue-50 border border-blue-200 rounded-lg">
                      <p className="text-sm font-medium text-blue-700 mb-2">🔧 Demo Mode Detected:</p>
                      <p className="text-xs text-blue-600 mb-3">
                        Since this is a demo environment, here's your reset token for testing:
                      </p>
                      <div className="bg-white p-2 rounded border">
                        <input
                          type="text"
                          value={resetToken}
                          readOnly
                          className="w-full text-xs font-mono bg-transparent border-none outline-none"
                        />
                      </div>
                      <button
                        onClick={() => {
                          navigator.clipboard.writeText(resetToken);
                          alert('Token copied to clipboard!');
                        }}
                        className="text-xs text-blue-600 hover:text-blue-800 underline mt-2 block"
                      >
                        📋 Copy Token
                      </button>
                      <button
                        onClick={() => setShowResetPassword(true)}
                        className="mt-3 w-full bg-blue-600 text-white py-2 px-4 rounded-lg text-sm font-medium hover:bg-blue-700 transition-colors"
                      >
                        🔑 Use Token to Reset Password
                      </button>
                    </div>
                  )}
                  
                  {!resetToken && (
                    <div className="mt-4">
                      <p className="text-sm text-green-600 mb-3">
                        💡 <strong>Next Steps:</strong>
                      </p>
                      <ol className="text-sm text-green-600 list-decimal list-inside space-y-1">
                        <li>Check your email inbox for the reset link</li>
                        <li>Click the reset link in the email</li>
                        <li>Enter your new password</li>
                      </ol>
                      <p className="text-xs text-green-500 mt-3">
                        If you don't receive the email within 5 minutes, please check your spam folder.
                      </p>
                    </div>
                  )}
                </div>
              )}
            </form>
          ) : showResetPassword ? (
            <ResetPasswordForm 
              resetToken={resetToken}
              onSuccess={() => {
                setShowResetPassword(false);
                setShowForgotPassword(false);
                setResetMessage("Password reset successfully! You can now sign in with your new password.");
              }}
              onBack={() => setShowResetPassword(false)}
            />
          ) : (
            <>
              {/* Post-signup success banner — QA-001 fix: the previous flow
                  quietly switched the auth panel to Sign-In mode without any
                  visible confirmation, leaving new users unsure whether their
                  account was created or where the verification email went. */}
              {resetMessage && !showForgotPassword && isLogin && (
                <div className="mb-5 p-4 bg-green-50 border border-green-200 rounded-xl" data-testid="signup-verify-banner">
                  <div className="flex items-start space-x-3">
                    <svg className="w-6 h-6 text-green-600 mt-0.5 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M3 8l7.89 4.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                    </svg>
                    <div className="flex-1 min-w-0">
                      <p className="text-green-800 font-semibold">{t('signupCheckEmailTitle')}</p>
                      <p className="text-green-700 text-sm mt-1 leading-relaxed">
                        {t('signupCheckEmailBody')}
                      </p>
                      {/* Resend verification link — visible only while we still remember which email just signed up */}
                      {pendingVerifyEmail && (
                        <div className="mt-2.5 text-sm">
                          {resendState === "sent" ? (
                            <span className="inline-flex items-center gap-1.5 text-green-700 font-medium" data-testid="resend-verify-sent">
                              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={2}>
                                <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
                              </svg>
                              {t('resendVerifySent')}
                            </span>
                          ) : (
                            <>
                              <span className="text-green-700">{t('resendVerifyPrompt')} </span>
                              <button
                                type="button"
                                onClick={async () => {
                                  if (resendState === "sending" || resendCooldown > 0) return;
                                  setResendState("sending");
                                  try {
                                    await axios.post(`${API}/auth/resend-verification`, { email: pendingVerifyEmail });
                                    setResendState("sent");
                                    setResendCooldown(30);
                                    const iv = setInterval(() => {
                                      setResendCooldown((c) => {
                                        if (c <= 1) { clearInterval(iv); return 0; }
                                        return c - 1;
                                      });
                                    }, 1000);
                                  } catch (err) {
                                    setResendState("error");
                                  }
                                }}
                                disabled={resendState === "sending" || resendCooldown > 0}
                                data-testid="resend-verify-btn"
                                className={`font-semibold underline underline-offset-2 ${resendState === "sending" || resendCooldown > 0 ? 'text-green-500 cursor-wait' : 'text-green-800 hover:text-green-900'}`}
                              >
                                {resendState === "sending"
                                  ? t('resendVerifySending')
                                  : (resendCooldown > 0 ? `${t('resendVerifyCooldown')} (${resendCooldown}s)` : t('resendVerifyCta'))}
                              </button>
                              {resendState === "error" && (
                                <p className="mt-1 text-red-600 text-xs" data-testid="resend-verify-error">{t('resendVerifyError')}</p>
                              )}
                            </>
                          )}
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              )}
              <form onSubmit={handleSubmit}>
                {!isLogin && (
                  <div className="mb-4">
                    <label className="block text-gray-700 text-sm font-medium mb-2">Name</label>
                    <input
                      type="text"
                      name="name"
                      value={formData.name}
                      onChange={handleInputChange}
                      className="w-full px-3 py-3 border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      required={!isLogin}
                    />
                  </div>
                )}
                
                <div className="mb-4">
                  <label className="block text-gray-700 text-sm font-medium mb-2">Email</label>
                  <input
                    type="email"
                    name="email"
                    value={formData.email}
                    onChange={handleInputChange}
                    className="w-full px-3 py-3 border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    required
                  />
                </div>

                <div className="mb-6">
                  <label className="block text-gray-700 text-sm font-medium mb-2">Password</label>
                  <PasswordInput
                    name="password"
                    value={formData.password}
                    onChange={handleInputChange}
                    required
                    minLength="6"
                    testIdBase="auth-password"
                  />
                  {isLogin && (
                    <div className="text-right mt-2">
                      <button
                        type="button"
                        onClick={() => {
                          setShowForgotPassword(true);
                          setForgotPasswordEmail(""); // Clear any previous email
                          setResetMessage(""); // Clear any previous message
                          setError(""); // Clear any error
                        }}
                        className="text-sm text-blue-600 hover:text-blue-800 font-medium"
                      >
                        Forgot Password?
                      </button>
                    </div>
                  )}
                </div>

                {/* Confirm Password Field for Signup */}
                {!isLogin && (
                  <div className="mb-6">
                    <label className="block text-gray-700 text-sm font-medium mb-2">Confirm Password</label>
                    <PasswordInput
                      name="confirmPassword"
                      value={formData.confirmPassword}
                      onChange={handleInputChange}
                      required
                      minLength="6"
                      testIdBase="auth-confirm-password"
                    />
                  </div>
                )}

                {/* Terms and Service Checkbox for Signup */}
                {!isLogin && (
                  <div className="mb-6">
                    <label className="flex items-start space-x-3 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={agreeToTerms}
                        onChange={(e) => setAgreeToTerms(e.target.checked)}
                        className="mt-1 w-4 h-4 text-blue-600 bg-gray-100 border-gray-300 rounded focus:ring-blue-500 focus:ring-2"
                      />
                      <span className="text-sm text-gray-700">
                        I agree to the{" "}
                        <button
                          type="button"
                          onClick={(e) => {
                            e.preventDefault();
                            setShowTermsModal(true);
                          }}
                          className="text-blue-600 hover:text-blue-800 underline font-medium"
                        >
                          Terms and Service
                        </button>
                      </span>
                    </label>
                  </div>
                )}

                <button
                  type="submit"
                  disabled={loading}
                  className="w-full bg-gradient-to-r from-blue-500 to-indigo-600 text-white py-3 rounded-xl font-medium hover:from-blue-600 hover:to-indigo-700 transition-all duration-200 transform hover:scale-105 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {loading ? "Processing..." : (isLogin ? "Sign In" : "Sign Up")}
                </button>
              </form>

              <div className="mt-6 text-center">
                <button
                  onClick={() => setIsLogin(!isLogin)}
                  className="text-blue-600 hover:text-blue-800 text-sm font-medium"
                >
                  {isLogin ? "Don't have an account? Sign up" : "Already have an account? Sign in"}
                </button>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

// Workspace-aware shell for stand-alone routes (e.g. /archives) so that
// pages mounted outside the main Components tree still get the same
// WorkspaceContext used by the WorkspaceSwitcher in the desktop header.
const ArchivesRouteShell = ({ children }) => {
  const { user } = useAuth();
  return <WorkspaceProvider user={user}>{children}</WorkspaceProvider>;
};



// Back Button Handler Component
const BackButtonHandler = () => {
  const navigate = useNavigate();

  useEffect(() => {
    // Push a dummy state to enable back button interception.
    // CRITICAL: preserve search + hash here — earlier versions used
    // `window.location.pathname` only, which stripped the `?token=...`
    // query off password-reset / email-verify URLs and caused the
    // ResetPasswordPage to flash "Invalid Reset Link" the moment the
    // user typed into the new-password field (the re-render re-read
    // `window.location.search`, now empty).
    const fullUrl = window.location.pathname + window.location.search + window.location.hash;
    window.history.pushState(null, '', fullUrl);

    const handleBackButton = () => {
      // Programmatic unwind of a modal/select-mode history entry — not a user back
      if (window.__historyBackUnwinding) {
        window.__historyBackUnwinding = false;
        return;
      }
      // Defer so a modal / select-mode hook (useHistoryBack) can claim this back first
      setTimeout(() => {
        if (Date.now() - (window.__historyBackConsumedAt || 0) < 500) {
          window.__historyBackConsumedAt = 0;
          return;
        }
        handleRouteBack();
      }, 0);
    };

    const handleRouteBack = () => {
      // Check if we're on a detail page
      const path = window.location.pathname;
      
      const isDetailPage = 
        path.includes('/tasks/') || 
        path.includes('/contacts/') ||
        path === '/archives' ||
        path === '/payment';

      if (isDetailPage) {
        // Prevent default back behavior — preserve search + hash too.
        const u = window.location.pathname + window.location.search + window.location.hash;
        window.history.pushState(null, '', u);
        // Navigate to home instead
        navigate('/', { replace: true });
      } else if (path === '/calendar' || path === '/contacts' || path === '/settings') {
        // From main tabs, go to home
        const u = window.location.pathname + window.location.search + window.location.hash;
        window.history.pushState(null, '', u);
        navigate('/', { replace: true });
      }
      // If already on home, let the default behavior (exit app)
    };

    window.addEventListener('popstate', handleBackButton);

    return () => {
      window.removeEventListener('popstate', handleBackButton);
    };
  }, [navigate]);

  return null;
};

// Protected Route Component
const ProtectedRoute = ({ children }) => {
  const { user } = useAuth();
  console.log("ProtectedRoute check - user:", user); // Add debug logging
  return user ? children : <Navigate to="/login" />;
};

function App() {
  // Register the read-only offline shell service worker exactly once.
  // The helper itself no-ops in development / non-secure origins.
  useEffect(() => {
    registerServiceWorker();
    // Best-effort: kick a queue drain on app boot in case we were offline at
    // shutdown last time. Only attempt it if we actually have a token —
    // otherwise we'd just thrash 401s.
    try {
      if (localStorage.getItem('token')) drainQueue();
    } catch { /* noop */ }
  }, []);

  return (
    <LanguageProvider>
      <AuthProvider>
        <ReminderManager />
        <OfflineBanner />
        <OfflineSyncManager />
        <ConfirmDialogHost />
        <Toaster position="top-center" richColors closeButton duration={3000} offset={24} />
        <div className="App app-shell">
          <BrowserRouter>
            <BackButtonHandler />
            <Routes>
              <Route path="/login" element={<Login />} />
              <Route path="/verify-email" element={<EmailVerificationPage />} />
              <Route path="/reset-password" element={<ResetPasswordPage />} />
              <Route path="/invitations/:token" element={<AcceptInvitationPage />} />
              <Route path="/share/task/:token" element={<PublicSharedTaskPage />} />
              <Route path="/payment" element={
                <ProtectedRoute>
                  <DataProvider>
                    <PaymentPage />
                  </DataProvider>
                </ProtectedRoute>
              } />
              <Route path="/payment-success" element={
                <ProtectedRoute>
                  <PaymentSuccessPage />
                </ProtectedRoute>
              } />
              <Route path="/payment-cancelled" element={
                <ProtectedRoute>
                  <PaymentCancelledPage />
                </ProtectedRoute>
              } />
              <Route path="/tasks/:taskId" element={
                <ProtectedRoute>
                  <ArchivesRouteShell>
                    <DataProvider>
                      <SwipeBack>
                        <TaskDetailsPage />
                      </SwipeBack>
                    </DataProvider>
                  </ArchivesRouteShell>
                </ProtectedRoute>
              } />
              <Route path="/contacts/:contactId" element={
                <ProtectedRoute>
                  <DataProvider>
                    <SwipeBack>
                      <ContactDetailsPage />
                    </SwipeBack>
                  </DataProvider>
                </ProtectedRoute>
              } />
              <Route path="/archives" element={
                <ProtectedRoute>
                  <ArchivesRouteShell>
                    <SwipeBack>
                      <ArchivesPage />
                    </SwipeBack>
                  </ArchivesRouteShell>
                </ProtectedRoute>
              } />
              <Route path="/*" element={
                <ProtectedRoute>
                  <Components />
                </ProtectedRoute>
              } />
            </Routes>
          </BrowserRouter>
        </div>
      </AuthProvider>
    </LanguageProvider>
  );
}

export default App;